/**
 * Painting the console in the workspace's colours.
 *
 * The server resolves the palette, the imported template and the accent into one
 * complete token set — see `api/app/org/theme.py` — and this writes that set onto
 * the document as CSS variables. Every colour in `tailwind.config.ts` reads them,
 * so one assignment retints the whole application.
 *
 * Two things are computed here rather than stored, because both are consequences
 * of the palette rather than choices anybody makes:
 *
 *   - **the ground.** A light `paper` needs `color-scheme: light`, or the
 *     browser draws dark scrollbars, dark form controls and a dark autofill
 *     over a white page.
 *   - **`onAccent`.** The text colour on an accent-filled button. White on a
 *     pale accent cannot be read, and a workspace is allowed a pale accent.
 *
 * Nothing here runs for the marketing site: that is one brand, ours, and its
 * colours are literals in the Tailwind config on purpose.
 */

import { useEffect } from 'react';

import type { ThemeTokens } from '../lib/session';

/** `#4f8ff7` -> `79 143 247`, which is the form `<alpha-value>` needs. */
function channels(hex: string): string | null {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;
  const value = parseInt(match[1], 16);
  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
}

function luminance(hex: string): number {
  const parsed = channels(hex);
  if (!parsed) return 0;
  const [r = 0, g = 0, b = 0] = parsed.split(' ').map((n) => {
    const fraction = Number(n) / 255;
    return fraction <= 0.04045 ? fraction / 12.92 : ((fraction + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const CSS_NAME: Record<string, string> = {
  paper: '--co-paper',
  surface: '--co-surface',
  surface2: '--co-surface2',
  ink: '--co-ink',
  accent: '--co-accent',
  accent2: '--co-accent2',
  warn: '--co-warn',
  danger: '--co-danger',
  good: '--co-good',
};

export function applyTheme(tokens: ThemeTokens | undefined): void {
  const root = document.documentElement;
  if (!tokens) return;

  for (const [name, variable] of Object.entries(CSS_NAME)) {
    const value = tokens[name];
    if (!value) continue;
    const parsed = channels(value);
    if (parsed) root.style.setProperty(variable, parsed);
  }

  // 0.45 rather than 0.5: the midpoint of *perceived* lightness sits below the
  // arithmetic one, and an accent right on the line reads better with dark text.
  if (tokens.accent) {
    root.style.setProperty(
      '--co-on-accent',
      luminance(tokens.accent) > 0.45 ? '17 17 17' : '255 255 255',
    );
  }

  if (tokens.paper) {
    root.dataset.ground = luminance(tokens.paper) > 0.4 ? 'light' : 'dark';
  }
}

/**
 * Applies the theme for as long as it is mounted, and puts the defaults back on
 * the way out — so moving between a tenant hostname and the apex in one session
 * does not leave the product site wearing a customer's colours.
 */
export default function BrandTheme({ tokens }: { tokens: ThemeTokens | undefined }) {
  useEffect(() => {
    if (!tokens) return undefined;
    applyTheme(tokens);
    return () => {
      const root = document.documentElement;
      for (const variable of Object.values(CSS_NAME)) root.style.removeProperty(variable);
      root.style.removeProperty('--co-on-accent');
      delete root.dataset.ground;
    };
    // The token values, not the object identity: /api/auth/me is re-read on
    // every navigation that changes what you may do, and a new object each time
    // would repaint the document on every one of them.
  }, [tokens && JSON.stringify(tokens)]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
